import { GQL } from '@aline/graphql';
import { isValid, parse } from 'date-fns';
import merge from 'lodash/merge';
import type { Location, NodeEntry, Path } from 'slate';
import { Editor, Node, Text } from 'slate';

import { CHECKBOX_BORDER_THICKNESS_PX, CHECKBOX_OPTION_NODEID_SEPARATOR } from '../../constants';
import type { UUID } from '../../types';
import { getISO8601DateString, isUUIDV4 } from '../../util';
import type {
  AlineText,
  CannedFieldElement,
  CheckboxElement,
  CheckboxOptionFieldElement,
  DBAssignable,
  DynamicBlockType,
  DynamicContentElement,
  DynamicElement,
  FakeElement,
  FieldsByAssignment,
  FieldValue,
  FieldWithAssignee,
  FieldWithAssignment,
  FieldWithValue,
  GQLAssignable,
  InputFieldElement,
  NonSigFieldDetails,
  ProcessTemplateField,
  SigFieldDetails,
  SignatureFieldElement,
  TriggerCondition,
  VariableSubset,
  VariableWithValue,
} from '../internal';
import {
  AlineTransforms,
  dynamicBlockType,
  getIsOptionsVariant,
  indexFieldsByNodeRefId,
  isCannedFieldElement,
  isCheckboxElement,
  isCheckboxOptionFieldElement,
  isDynamicElement,
  isDynamicEntry,
  isInputFieldElement,
  isSignatureFieldElement,
  Queries,
} from '../internal';
import { defaultSignatureFieldHeightPx, defaultSignatureFieldWidthPx } from '../render-model';

export function isNonSigFieldDetails(details: DynamicNodeDetails): details is NonSigFieldDetails {
  return 'variant' in details && details.variant !== GQL.FieldVariant.Signature;
}

export function isSigFieldDetails(details: DynamicNodeDetails): details is SigFieldDetails {
  return 'variant' in details && details.variant === GQL.FieldVariant.Signature;
}

export function isDynamicBlockDetails(details: DynamicNodeDetails): details is DynamicBlockDetails {
  return 'type' in details && details.type === dynamicBlockType;
}

export type InputFieldDetails = NonSigFieldDetails | SigFieldDetails;
export interface DynamicBlockDetails {
  count: number;
  // Note: For inProps, these locations are the locations of the dynamic block *inside each triggerJSON*.  For inChildren, the paths where the dynamic block occurs in children.
  locations: Path[];
  triggeredConditions: TriggerCondition[];
  triggerIndex: DynamicContentElement['triggerIndex'];
  type: DynamicBlockType;
}
export type DynamicNodeDetails = InputFieldDetails | DynamicBlockDetails;
// DynamicBlock.fieldId or InputField.fieldId -> FieldDetails.
export type FieldIdToDetails = Record<UUID, DynamicNodeDetails>;
export interface CannedFieldDetails extends Pick<CannedFieldElement, 'type' | 'variant'> {
  count: number;
  locations: Path[];
  text: string;
}
export type CannedFieldIdToDetails = Record<UUID, CannedFieldDetails>;
export interface FieldsInJSON {
  cannedInChildren: CannedFieldIdToDetails;
  inChildren: FieldIdToDetails;
  inProps: FieldIdToDetails;
}

export type LocationDirection = 'after' | 'before';

/**
 * Utility reduce callback for producing FieldIdToDetails about nested entries in the docJSON.
 */
function reduceDynamicEntry(args: {
  isInChildren: boolean;
  getDynamic?: 'inChildren' | 'nested';
}): (acc: FieldsInJSON, entry: NodeEntry<DynamicElement>) => FieldsInJSON {
  const { getDynamic, isInChildren } = args;

  return function reduceEntry(acc, [node, path]) {
    const key: keyof FieldsInJSON = isInChildren ? 'inChildren' : 'inProps';
    const existing = acc[key];
    const { count = 0, locations = [] } = existing[node.fieldId] ?? {};

    switch (node.type) {
      case 'dynamic-block': {
        const { triggerIndex, triggers = [] } = node;
        const nestedFieldsInJSON: FieldsInJSON =
          getDynamic === 'nested'
            ? Queries.getDynamicNestedFieldDetails(node, { getDynamic })
            : { cannedInChildren: {}, inChildren: {}, inProps: {} };
        const selectedTrigger = triggers.find((_trigger, index) => triggerIndex === index);
        const triggeredConditions = selectedTrigger?.conditions ?? [];
        const dynamicBlockDetails: DynamicBlockDetails = {
          count: count + 1,
          locations: [...locations, path],
          triggerIndex,
          triggeredConditions,
          type: node.type,
        };

        return merge({}, nestedFieldsInJSON, {
          ...acc,
          [key]: {
            ...existing,
            [node.fieldId]: dynamicBlockDetails,
          },
        });
      }
      case 'INPUT': {
        switch (node.variant) {
          case GQL.FieldVariant.Signature: {
            const allSignedKey = 'areAllSigned' satisfies keyof SigFieldDetails;
            const anySignedKey = 'areAnySigned' satisfies keyof SigFieldDetails;
            // If there is a previous sig field with the same fieldId, compare its signing state to the current.
            const prevSigDetails: DynamicNodeDetails | Record<string, unknown> =
              existing[node.fieldId] ?? {};
            const currentNodeIsSigned = Boolean(node.signatureImgId);
            const areAnySigned =
              currentNodeIsSigned ||
              (anySignedKey in prevSigDetails && prevSigDetails.areAnySigned);
            const areAllSigned =
              currentNodeIsSigned &&
              (allSignedKey in prevSigDetails ? prevSigDetails.areAllSigned : true);

            const sigInputDetails: SigFieldDetails = {
              areAllSigned,
              areAnySigned,
              count: count + 1,
              locations: [...locations, path],
              text: '',
              type: node.type,
              variant: node.variant,
            };

            return {
              ...acc,
              [key]: { ...existing, [node.fieldId]: sigInputDetails },
            };
          }
          default: {
            const nonSigInputDetails: InputFieldDetails = {
              count: count + 1,
              locations: [...locations, path],
              text: Node.string(node),
              type: node.type,
              variant: node.variant,
            };

            return { ...acc, [key]: { ...existing, [node.fieldId]: nonSigInputDetails } };
          }
        }
      }
    }
  };
}

function reduceCannedFieldEntry(
  acc: CannedFieldIdToDetails,
  [node, path]: NodeEntry<CannedFieldElement>
): CannedFieldIdToDetails {
  const existing = acc[node.cannedFieldId];
  return {
    ...acc,
    [node.cannedFieldId]: {
      count: (existing?.count ?? 0) + 1,
      locations: [...(existing?.locations ?? []), path],
      text: Node.string(node),
      type: node.type,
      variant: node.variant,
    },
  };
}

function reduceFieldEntry(args: {
  getDynamic?: 'inChildren' | 'nested';
}): (acc: FieldsInJSON, entry: NodeEntry) => FieldsInJSON {
  const reduceDynamic = reduceDynamicEntry({ getDynamic: args.getDynamic, isInChildren: true });

  return function reduceEntry(acc, entry) {
    const [node, path] = entry;
    if (isCannedFieldElement(node))
      return {
        ...acc,
        cannedInChildren: reduceCannedFieldEntry(acc.cannedInChildren, [node, path]),
      };
    if (isDynamicEntry(entry)) return reduceDynamic(acc, entry);
    return acc;
  };
}

export const fieldQueries = {
  /**
   * Gets all field details in a Document indexed by fieldId.
   */
  fieldDetails: (
    editor: Editor,
    options: { getDynamic?: 'inChildren' | 'nested'; include?: 'active' | 'all' } = {}
  ): FieldsInJSON => {
    const { getDynamic, include = 'active' } = options;
    const match = getDynamic
      ? (node: Node) => isDynamicElement(node) || isCannedFieldElement(node)
      : (node: Node) => isInputFieldElement(node) || isCannedFieldElement(node);
    if (include === 'all') {
      const fieldEntries = [
        ...Editor.nodes(editor, {
          at: [],
          match,
          mode: 'all',
          voids: true,
        }),
      ];

      return fieldEntries.reduce<FieldsInJSON>(reduceFieldEntry({ getDynamic }), {
        cannedInChildren: {},
        inChildren: {},
        inProps: {},
      });
    }

    // Include "active" gets only nodes that are in "active root entries"
    // (i.e., the Document and any active headers/footers).
    const activeRootEntries = Queries.getActiveRootEntries(editor);
    const activeFieldEntries = activeRootEntries.flatMap(([, path]) => [
      ...Editor.nodes(editor, {
        at: path,
        match,
        mode: 'all',
        voids: true,
      }),
    ]);

    return activeFieldEntries.reduce<FieldsInJSON>(reduceFieldEntry({ getDynamic }), {
      cannedInChildren: {},
      inChildren: {},
      inProps: {},
    });
  },

  /**
   * Returns a boolean indicating whether fieldsInJSON has signed signatures.
   */
  fieldsInJSONHasSignedSignatures: (fieldsInJSON: FieldsInJSON): boolean => {
    const { inChildren, inProps } = fieldsInJSON;
    const nodeDetailsList = [...Object.values(inChildren), ...Object.values(inProps)];

    return nodeDetailsList.some(details => isSigFieldDetails(details) && details.areAnySigned);
  },

  /**
   * Gets all assigned elements for a given User in a Document.
   */
  getAssignedDynamicElements: (
    editor: Editor,
    options: {
      inputFields: FieldWithAssignee[];
      userId: UUID;
      at?: Location;
      includeOnlyIncomplete?: boolean;
      match?: (n: DynamicElement) => boolean;
    }
  ): { element: DynamicElement; isComplete: boolean }[] => {
    const {
      at = editor.selection,
      includeOnlyIncomplete = false,
      inputFields,
      match = () => true,
      userId,
    } = options;
    if (!at) return [];

    const nodeRefIdToField = indexFieldsByNodeRefId(inputFields);
    const isAssignedPred = Queries.getAssignedElementPredicate(editor, {
      includeOnlyIncomplete,
      nodeRefIdToField,
      userId,
    });
    const entries = [
      ...Editor.nodes<DynamicElement>(editor, {
        at,
        match: n => isDynamicElement(n) && isAssignedPred(n) && match(n),
        voids: true,
      }),
    ];

    return entries.map(([element]) => {
      const dBField = nodeRefIdToField[element.fieldId];
      if (!dBField) return { element, isComplete: false };

      return { element, isComplete: Queries.getIsDynamicElementCompleted(element, dBField) };
    });
  },

  /**
   * Returns a predicate function used to find/filter nodes that are assigned to a given user.
   */
  getAssignedElementPredicate: (
    _editor: Editor,
    options: {
      includeOnlyIncomplete: boolean;
      nodeRefIdToField: Record<UUID, FieldWithAssignee>;
      userId: UUID;
    }
  ): ((node: DynamicElement) => boolean) => {
    const { includeOnlyIncomplete, nodeRefIdToField, userId } = options;

    return node => {
      const { fieldId: nodeRefId } = node;
      const inputField = nodeRefIdToField[nodeRefId];
      if (!inputField) return false;

      const isCompleted = Queries.getIsDynamicElementCompleted(node, inputField);
      const isUserAssignee = inputField.assignee?.id === userId;

      return includeOnlyIncomplete ? isUserAssignee && !isCompleted : isUserAssignee;
    };
  },

  getCheckboxFieldElements(optionEntry: NodeEntry<CheckboxOptionFieldElement>): {
    checkboxEntry: NodeEntry<CheckboxElement> | undefined;
    labelTextEntries: NodeEntry<AlineText>[];
  } {
    const [optionNode, optionPath] = optionEntry;
    const checkboxIdx = optionNode.children.findIndex(isCheckboxElement);
    if (checkboxIdx < 0) return { checkboxEntry: undefined, labelTextEntries: [] };

    const checkbox = optionNode.children[checkboxIdx];
    if (!isCheckboxElement(checkbox)) return { checkboxEntry: undefined, labelTextEntries: [] };

    return {
      checkboxEntry: [checkbox, optionPath.concat(checkboxIdx)],
      labelTextEntries: optionNode.children.flatMap((child, index) =>
        index > checkboxIdx && Text.isText(child) ? [[child, optionPath.concat(index)]] : []
      ),
    };
  },

  getCheckboxFieldOptionTextByOptionId(editor: Editor, fieldId: string): Map<string, string[]> {
    const map = new Map<string, string[]>();
    const checkboxOptionNodeEntries = Editor.nodes(editor, {
      at: [],
      match: (node): node is CheckboxOptionFieldElement =>
        isCheckboxOptionFieldElement(node) && node.fieldId === fieldId,
      voids: true,
    });

    for (const optionEntry of checkboxOptionNodeEntries) {
      const [optionNode] = optionEntry;
      if (!optionNode.id) continue;

      const { labelTextEntries } = Queries.getCheckboxFieldElements(optionEntry);
      if (!labelTextEntries.length) continue;

      const optionLabelText = labelTextEntries.map(([textNode]) => textNode.text).join('');
      const existing = map.get(optionNode.id);
      if (existing) existing.push(optionLabelText);
      else map.set(optionNode.id, [optionLabelText]);
    }
    return map;
  },

  getCheckboxFieldValue: (checkboxField: VariableSubset): string[] =>
    checkboxField.selectedAnswerIds ?? [],

  getCheckboxOptionNodeId: (variableNodeId: UUID, optionId: UUID): UUID =>
    `${variableNodeId}${CHECKBOX_OPTION_NODEID_SEPARATOR}${optionId}`,

  /**
   * Get a datefield's value.  Important to return as ISO 8601 string.
   */
  getDateFieldValue: (
    dateField: Pick<GQL.DocumentVariable, 'label' | '__typename' | 'variant'>,
    inlineText: string
  ): string | null => {
    if (inlineText === dateField.label) return null;

    const date = parse(inlineText, 'MMMM d, yyyy', new Date());

    return isValid(date) ? getISO8601DateString(date) : null;
  },

  /**
   * Gets FieldDetails from nested trigger.contentJSON inside dynamic elements.
   */
  getDynamicNestedFieldDetails(
    dynamicContentElement: DynamicContentElement,
    options: { getDynamic?: 'inChildren' | 'nested' } = {}
  ): FieldsInJSON {
    const { getDynamic } = options;
    const { triggers = [] } = dynamicContentElement;

    return triggers.reduce<FieldsInJSON>(
      (acc, trigger) => {
        const fakeElement: FakeElement = {
          children: trigger.contentJSON,
          suggestedEdits: [],
          type: 'fake',
        };
        const entries = [...Node.nodes(fakeElement)];

        return entries
          .filter(isDynamicEntry)
          .reduce(reduceDynamicEntry({ getDynamic, isInChildren: false }), acc);
      },
      { cannedInChildren: {}, inChildren: {}, inProps: {} }
    );
  },

  /**
   * Get an input field's value.
   */
  getFieldValue: (field: VariableSubset, inlineText: string): FieldValue => {
    switch (field.variant) {
      case GQL.MetadataVariant.Email:
      case GQL.MetadataVariant.Text:
        return Queries.getTextFieldValue(field, inlineText);
      case GQL.MetadataVariant.Date:
        return Queries.getDateFieldValue(field, inlineText);
      case GQL.MetadataVariant.Checkbox:
        return null;
      case GQL.MetadataVariant.Currency:
      case GQL.MetadataVariant.Decimal:
        return Queries.getNumberFieldValue(field, inlineText);
      case GQL.MetadataVariant.Options:
      case GQL.MetadataVariant.RadioOptions:
        return Queries.getOptionsFieldValue(field, inlineText);
    }
  },

  /**
   * Organizes all field nodes by "assignment" (userId/roleId combo).  Useful for determining if a particular "assignment" has completed
   * all fields of a given type (signature, non-signature, etc.).
   */
  getFieldsInJSONByAssignment: (
    editor: Editor,
    options: {
      fields: FieldWithAssignment[];
      include?: 'all' | 'active';
    }
  ): FieldsByAssignment => {
    const { fields, include = 'active' } = options;
    // getDynamic: 'inChildren' will only crawl DCs in editor.children, not triggerJSONs of those DCs.
    const { inChildren } = Queries.fieldDetails(editor, { getDynamic: 'inChildren', include });
    const fieldsIndexedByNodeRefId = indexFieldsByNodeRefId(fields);

    return Object.entries(inChildren).reduce<FieldsByAssignment>((acc, [fieldId, nodeDetails]) => {
      const field = fieldsIndexedByNodeRefId[fieldId];
      if (!field) return acc;

      const assignedUserId = field.assignee?.id ?? null;
      const assignedRoleId = field.assigneeRole?.id ?? null;
      const key = Queries.getKeyForAssignable(field);
      const existing = acc[key] ?? {};
      const existingFieldDetailsList = existing?.fieldDetailsList ?? [];

      switch (nodeDetails.type) {
        case 'INPUT': {
          switch (nodeDetails.variant) {
            case GQL.FieldVariant.Signature:
              acc[key] = {
                assignedRoleId,
                assignedUserId,
                fieldDetailsList: [...existingFieldDetailsList, nodeDetails],
              };
              return acc;
            default:
              acc[key] = {
                assignedRoleId,
                assignedUserId,
                fieldDetailsList: [
                  ...existingFieldDetailsList,
                  {
                    ...nodeDetails,
                    isComplete:
                      field.__typename !== 'SignatureField' && field.label !== nodeDetails.text,
                  },
                ],
              };
              return acc;
          }
        }
        default:
          acc[key] = {
            assignedRoleId,
            assignedUserId,
            fieldDetailsList: [...existingFieldDetailsList, nodeDetails],
          };
          return acc;
      }
    }, {});
  },

  /**
   * Gets the value a field should be when "initialized".
   */
  getInitializedFieldValue: (args: {
    fields: Record<UUID, ProcessTemplateField>;
    inputField: InputFieldElement;
  }): string => {
    const { fields, inputField } = args;
    const fieldData = fields[inputField.fieldId];
    const inputFieldText = Node.string(inputField);
    const isOptionsField = getIsOptionsVariant(fieldData?.variant);
    const initialValueText = getInitialValueText(fieldData?.initialValue);
    const defaultAnswerText = isOptionsField ? fieldData?.defaultAnswer?.optionText : null;

    return initialValueText || defaultAnswerText || fieldData?.label || inputFieldText;
  },

  /**
   * Returns whether a DB Field is completed.
   */
  getIsDBFieldCompleted: (variable: Omit<VariableWithValue, 'label'>): boolean => {
    switch (variable.variant) {
      case GQL.MetadataVariant.Options:
      case GQL.MetadataVariant.RadioOptions:
        return Boolean(variable.selectedAnswer);
      case GQL.MetadataVariant.Checkbox: {
        const count = variable.selectedAnswers?.length ?? 0;
        return Queries.getIsCheckboxComplete({
          ...variable,
          selectedCount: count,
        });
      }
      default:
        return Boolean(variable.value);
    }
  },

  /**
   * Returns whether a DynamicElement is completed.
   */
  getIsDynamicElementCompleted: (
    dynamicElement: DynamicElement,
    dBField: FieldWithValue
  ): boolean => {
    switch (dynamicElement.type) {
      case 'INPUT': {
        switch (dynamicElement.variant) {
          case GQL.FieldVariant.Signature:
            return isSignatureFieldElement(dynamicElement) && !!dynamicElement.signatureImgId;
          case GQL.FieldVariant.Checkbox:
            return (
              dBField.__typename !== 'SignatureField' && Queries.getIsDBFieldCompleted(dBField)
            );
          default: {
            const fieldText = Node.string(dynamicElement);

            return dBField.__typename !== 'SignatureField' && dBField.label !== fieldText;
          }
        }
      }
      case 'dynamic-block':
        return dynamicElement.triggerIndex !== 'not-set';
    }
  },

  /**
   * Gets a unique key for an "assignable" (Field, Task, etc.)
   */
  getKeyForAssignable(assignable: GQLAssignable | DBAssignable): string {
    if ('assignee' in assignable) {
      const assignedUserId = assignable.assignee?.id ?? null;
      const assignedRoleId = assignable.assigneeRole?.id ?? null;
      return `${assignedUserId}:${assignedRoleId}`;
    }

    return `${assignable.assignedUserId}:${assignable.assignedRoleId}`;
  },

  /**
   * Get the next dynamic element assigned to a given user.
   */
  getNextAssignedDynamicElement: (
    editor: Editor,
    options: {
      inputFields: FieldWithAssignee[];
      userId: UUID;
      at?: Location;
      direction?: LocationDirection;
      includeOnlyIncomplete?: boolean;
      match?: (n: DynamicElement) => boolean;
    }
  ): NodeEntry<DynamicElement> | void => {
    return AlineTransforms.withoutNormalizing(editor, () => {
      const { includeOnlyIncomplete = false, inputFields, match = () => true, userId } = options;
      /**
       * Index inputFields from the DB by nodeRefId first so it's a fast lookup of nodeRefId -> field when
       * AlineTransforms.getNextField iterates through field elements in the JSON.
       */
      const nodeRefIdToField = indexFieldsByNodeRefId(inputFields);
      const isAssignedPred = Queries.getAssignedElementPredicate(editor, {
        includeOnlyIncomplete,
        nodeRefIdToField,
        userId,
      });

      return Queries.getNextDynamicElement(editor, {
        ...options,
        match: n => isAssignedPred(n) && match(n),
      });
    });
  },

  /**
   * Get the next dynamic element *after* a location.
   * The default match simply finds the next dynamic element after the selection.
   */
  getNextDynamicElement: (
    editor: Editor,
    options: {
      at?: Location;
      direction?: LocationDirection;
      match?: (n: DynamicElement) => boolean;
    }
  ): NodeEntry<DynamicElement> | void => {
    return AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, direction = 'after', match = () => true } = options;
      if (!at) return;

      const dynamicEntriesAbove = [
        ...Editor.levels(editor, { at, match: isDynamicElement, voids: true }),
      ];

      /**
       * We want to exclude all dynamic entries above at b/c we want a match *after/before* the location we are in.
       */
      const query = direction === 'after' ? Editor.next : Editor.previous;
      const matchEntry = query<DynamicElement>(editor, {
        at,
        match: n =>
          dynamicEntriesAbove.every(([dynamicNode]) => n !== dynamicNode) &&
          isDynamicElement(n) &&
          match(n),
        mode: 'all',
        voids: true,
      });
      if (!matchEntry) {
        // We've already searched to the end of the document if direction === 'after' and vice/versa if direction === 'before'.  Now search the rest of the document.
        const documentLocation =
          direction === 'after' ? Editor.start(editor, []) : Editor.end(editor, []);

        return query<DynamicElement>(editor, {
          at: documentLocation,
          match: n => isDynamicElement(n) && match(n),
          mode: 'all',
          voids: true,
        });
      }

      return matchEntry;
    });
  },

  /**
   * Get a numberfield's value. Stores the inline text as typed (any non-empty
   * non-label string). Digit warnings are UI-only and must not clear values.
   */
  getNumberFieldValue: (
    numberField: Pick<GQL.DocumentVariable, 'label' | '__typename' | 'variant'>,
    inlineText: string
  ): string | null => {
    if (!inlineText || inlineText === numberField.label) return null;
    return inlineText;
  },

  getOptionsFieldValue(optionsField: VariableSubset, inlineText: string): UUID | null {
    if (inlineText === optionsField.label) return null;

    const selectedOption = optionsField.answerOptions.options.find(
      o => o.optionText === inlineText
    );

    return selectedOption ? selectedOption.id : null;
  },

  /**
   * Get all signature node entries at a Location.
   * Optional `filter` predicate can be used to further filter the results.
   */
  getSignatureEntries(
    editor: Editor,
    options: {
      at?: Location;
      filter?: (node: SignatureFieldElement) => boolean;
    }
  ): NodeEntry<SignatureFieldElement>[] {
    const { at = editor.selection, filter = () => true } = options;
    if (!at) return [];

    return [
      ...Editor.nodes<SignatureFieldElement>(editor, {
        at,
        match: n => isSignatureFieldElement(n) && filter(n),
        voids: true,
      }),
    ];
  },

  /**
   * The height and width of a signature field.
   */
  getSignatureImageDimensions: (
    signatureField: Pick<SignatureFieldElement, 'imgWidthPx' | 'imgHeightPx'>
  ): { imgHeightPx: number; imgWidthPx: number } => {
    return {
      imgHeightPx: signatureField.imgHeightPx ?? defaultSignatureFieldHeightPx,
      imgWidthPx: signatureField.imgWidthPx ?? defaultSignatureFieldWidthPx,
    };
  },

  /**
   * Get a textfield's value.
   */
  getTextFieldValue: (
    textField: Pick<GQL.DocumentVariable, 'label' | '__typename' | 'variant'>,
    inlineText: string
  ): string | null => {
    return inlineText && inlineText !== textField.label ? inlineText : null;
  },

  isCheckboxOptionCompositeFieldName(fieldName: string): boolean {
    const parts = fieldName.split(CHECKBOX_OPTION_NODEID_SEPARATOR);
    if (parts.length !== 2) return false;
    return isUUIDV4(parts[0]) && isUUIDV4(parts[1]);
  },

  parseCheckboxOptionNodeId: (nodeId: string): { optionId: UUID; variableNodeId: UUID } | null => {
    const parts = nodeId.split(CHECKBOX_OPTION_NODEID_SEPARATOR);
    if (parts.length !== 2) return null;
    const [variableNodeId, optionId] = parts;
    if (!isUUIDV4(variableNodeId) || !isUUIDV4(optionId)) return null;
    return { optionId: optionId as UUID, variableNodeId: variableNodeId as UUID };
  },

  pickCanonicalCheckboxOptionLabel(normalized: string[], storedOptionText: string): string {
    if (!normalized.length) return storedOptionText;
    return normalized.find(t => t !== storedOptionText) ?? normalized[0];
  },

  rangeIncludesCheckboxElement(editor: Editor, at: Location): boolean {
    const range = Editor.range(editor, at);
    return !Editor.nodes(editor, {
      at: range,
      match: node => isCheckboxElement(node) || isCheckboxOptionFieldElement(node),
      voids: true,
    }).next().done;
  },

  renderedCheckboxSizePx: (text: Partial<Omit<AlineText, 'text' | 'comments'>>): number => {
    const fontSizePx = Queries.renderedFontSize(text);
    return Math.max(1, fontSizePx - 2 * CHECKBOX_BORDER_THICKNESS_PX);
  },
};

function getInitialValueText(initialValue: ProcessTemplateField['initialValue']): string | null {
  if (!initialValue) return null;

  switch (initialValue.type) {
    case 'answer-option':
      return initialValue.answerOption.optionText;
    case 'field-value':
      return initialValue.value;
    // Checkbox fields are initialized through their options, never through a text value.
    case 'checkbox':
      return null;
  }
}
